/**
 * What each run of the region's network actually *is* — the classification the
 * sheet needs before it can ink a river differently from a road.
 *
 * **The source data already knows.** A baked face carries `anchorIndex`, the
 * `BARON.PAL` index the original draws it with, flat and unshaded
 * (docs/formats/world-terrain.md §7.2: scenery renders at `colourFront` with no
 * shade offset — VERIFIED against the running original under DOSBox-X). The
 * terrain corpus uses that byte as a feature tag, and the census over every
 * placed object of all five regions comes out cleanly separated:
 *
 * | `anchorIndex` | `BARON.PAL` | faces | what it is |
 * |---|---|---|---|
 * | 4 | `#304965` | 535 triangles | rivers, estuaries and the sea |
 * | 67, 68, 69, 70, 71, 72, 78 | `#2c2c2c`…`#8a8a8a` | 838 triangles + 362 line faces | roads — the neutral-grey ramp, the only greys the terrain uses |
 * | 124 | `#6d553c` | 111 triangles | field boundaries (one object per region is nothing else) |
 * | 112–115, 128–144 | olives and greens | 331 triangles | the landcover plates: woods, heath and cultivated ground |
 *
 * §7.2's own table names three of those four in as many words — river 4, roads
 * 67/68/70, field 124 — so this module is that table read as a lookup rather
 * than as prose. Nothing here is fitted: every boundary falls in a gap the
 * census leaves empty.
 *
 * Anything else (a building's kerb at 120, a hangar's trim at 31) stays
 * `"other"` and keeps the sheet's original undifferentiated brown, because the
 * evidence for those is a handful of segments and a guess would be worse than
 * a plain line.
 */

/** The classes the sheet inks separately. `"other"` is "printed detail we cannot name". */
export type MapFeature = "water" | "road" | "field" | "wood" | "other";

/**
 * Rivers, estuaries and the sea, all one index. §7.2 measured it off the
 * running original: Verdun's river renders in `#304965` at exactly one native
 * pixel wide, and `colourFront` for those faces is 4.
 */
export const WATER_PALETTE_INDEX = 4;

/**
 * The neutral-grey ramp. §7.2 verifies 67/68/70 as roads from capture; the
 * census adds 69, 71, 72 and 78 (the same ramp, `#3c3c3c`/`#515151`/`#595959`/
 * `#8a8a8a`) on the line-face encoding of the same feature — 362 of the
 * corpus's 383 line faces are in it. No other terrain colour in the corpus is
 * a neutral grey, which is what makes a range safe here: the ramp is the road
 * ramp and nothing shares it.
 */
const ROAD_FIRST = 64;
const ROAD_LAST = 79;

/** Field boundaries. One object per region (`TERR5-10`, `TERR2-17`, `TERR3-17`, `TERR4-23`) is built of nothing but this index. */
const FIELD_PALETTE_INDEX = 124;

/**
 * The landcover plates' two bands: 112–115 are §7.2's "landform faces"
 * (`#615934`, `#655d38`) and 128–144 the olive-to-green block the big plates
 * (`TERR5-72`, `TERR1-71`, `TERR2-76`, …) are built from. `FIELD_PALETTE_INDEX`
 * sits inside the second band's numeric span and is tested first, which is why
 * the order of the checks below is load-bearing.
 */
const WOOD_BANDS: readonly (readonly [number, number])[] = [
  [112, 115],
  [128, 144],
];

function inBand(index: number, bands: readonly (readonly [number, number])[]): boolean {
  return bands.some(([first, last]) => index >= first && index <= last);
}

/** The feature class a baked face's `anchorIndex` names. */
export function featureOfPaletteIndex(index: number): MapFeature {
  if (index === WATER_PALETTE_INDEX) return "water";
  if (index >= ROAD_FIRST && index <= ROAD_LAST) return "road";
  if (index === FIELD_PALETTE_INDEX) return "field";
  if (inBand(index, WOOD_BANDS)) return "wood";
  return "other";
}
